import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  FiHome, FiUsers, FiShoppingBag, FiSettings, 
  FiMapPin, FiGrid, FiPackage, FiBriefcase,
  FiTrendingUp, FiFileText, FiTag, FiCreditCard,
  FiImage, FiUserCheck, FiSmartphone, FiLogOut,
  FiMenu, FiX, FiUser,FiBell, FiKey, FiCalendar
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { socket } from '../../socket/socket';
import api from '../../utils/api';
import { TbCurrencyRupee } from 'react-icons/tb';


const Sidebar = ({ isCollapsed: externalCollapsed, setIsCollapsed: setExternalCollapsed, isMobileOpen: externalMobileOpen, setIsMobileOpen: setExternalMobileOpen }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(externalCollapsed || false);
  const [isMobileOpen, setIsMobileOpen] = useState(externalMobileOpen || false);
  const [canViewReports, setCanViewReports] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [pendingBillApprovalsCount, setPendingBillApprovalsCount] = useState(0);
  const [pendingBillUpdateRequestsCount, setPendingBillUpdateRequestsCount] = useState(0);

  // Sync with external state
  useEffect(() => {
    if (externalCollapsed !== undefined) {
      setIsCollapsed(externalCollapsed);
    }
  }, [externalCollapsed]);

  useEffect(() => {
    if (externalMobileOpen !== undefined) {
      setIsMobileOpen(externalMobileOpen);
    }
  }, [externalMobileOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Only trigger shortcuts when not typing in input fields
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.isContentEditable) {
        return;
      }

      const key = event.key.toLowerCase();
      
      // Super Admin shortcuts
      if (user?.role === 'superadmin') {
        if (event.altKey) {
          switch (key) {
            case 'a': // Dashboard
              event.preventDefault();
              navigate('/superadmin/dashboard');
              break;
            case 'v': // All Vendors
              event.preventDefault();
              navigate('/superadmin/vendors');
              break;
            case 'p': // Pending Vendors
              event.preventDefault();
              navigate('/superadmin/vendors/pending');
              break;
            case 'o': // Vendor Owners
              event.preventDefault();
              navigate('/superadmin/vendor-owners');
              break;
            case 'd': // Delivery Requests
              event.preventDefault();
              navigate('/superadmin/delivery-requests');
              break;
            case 'u': // App Users
              event.preventDefault();
              navigate('/superadmin/app-users');
              break;
            case 'r': // Room Management
              event.preventDefault();
              navigate('/superadmin/room-management');
              break;
            case 'm': // Banquet Management
              event.preventDefault();
              navigate('/superadmin/banquet');
              break;
            case 'b': // Banner Management
              event.preventDefault();
              navigate('/superadmin/banners');
              break;
            case 's': // Subscription Plans
              event.preventDefault();
              navigate('/superadmin/subscription-plans');
              break;
            case 'l': // Plan Requests
              event.preventDefault();
              navigate('/superadmin/plan-requests');
              break;
            case 't': // Team Management
              event.preventDefault();
              navigate('/superadmin/sales-officers');
              break;
            case 'i': // Restaurant Visits
              event.preventDefault();
              navigate('/superadmin/restaurant-visits');
              break;
            case 'j': // Job Management
              event.preventDefault();
              navigate('/superadmin/jobs');
              break;
            case 'n': // Notifications
              event.preventDefault();
              navigate('/superadmin/notifications');
              break;
            case 'p': // Profile
              event.preventDefault();
              navigate('/superadmin/profile');
              break;
          }
        }
      }
      
      // Vendor Owner shortcuts
      else if (user?.role === 'vendorOwner') {
        if (event.altKey) {
          switch (key) {
            case 'w': // Dashboard
              event.preventDefault();
              navigate('/vendor-owner/dashboard');
              break;
            case 'r': // My Restaurants
              event.preventDefault();
              navigate('/vendor-owner/restaurants');
              break;
            case 'm': // Menu Management
              event.preventDefault();
              navigate('/vendor-owner/menu-management');
              break;
            case 'p': // Vendor Permissions
              event.preventDefault();
              navigate('/vendor-owner/vendor-permissions');
              break;
            // case 's': // Sales Report
            //   event.preventDefault();
            //   navigate('/vendor-owner/sales');
            //   break;
            // case 'a': // Analytics
            //   event.preventDefault();
            //   navigate('/vendor-owner/analytics');
            //   break;
            case 'x': // Reports
              event.preventDefault();
              navigate('/vendor-owner/reports');
              break;
            // case 'o': // All Orders
            //   event.preventDefault();
            //   navigate('/vendor-owner/orders');
            //   break;
          }
        }
      }
      
      // Vendor shortcuts (already implemented)
      else if (user?.role === 'vendor') {
        // Alt+A for Add Order (works on all vendor pages) - using Alt to avoid Ctrl conflicts
        if (event.altKey && (key === 'a' || key === 'A')) {
          // Only trigger if not typing in input fields
          if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.isContentEditable) {
            console.log('Ignoring - typing in input field');
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          // Navigate to orders page and open add order modal
          navigate('/vendor/orders');
          // Use setTimeout to ensure the page loads before opening modal
          setTimeout(() => {
            // Find and click the Add Order button or dispatch custom event
            const addOrderButton = document.querySelector('[data-add-order-btn]') || 
                                 document.querySelector('button:has([class*="plus"])') ||
                                 document.querySelector('button:has(.fi-plus)');
            if (addOrderButton) {
              addOrderButton.click();
            } else {
              // Fallback: dispatch custom event that OrdersManagement can listen for
              window.dispatchEvent(new CustomEvent('openAddOrderModal'));
            }
          }, 100);
          return;
        }
        
        // Alt+Y for Add Category (works on all vendor pages)
        if (event.altKey && (key === 'y' || key === 'Y')) {
          // Only trigger if not typing in input fields
          if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.isContentEditable) {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          // Navigate to menu page and open add category modal
          navigate('/vendor/menu');
          setTimeout(() => {
            const addCategoryButton = document.querySelector('[data-add-category-btn]');
            if (addCategoryButton) {
              addCategoryButton.click();
            } else {
              window.dispatchEvent(new CustomEvent('openAddCategoryModal'));
            }
          }, 100);
          return;
        }
        
        // Alt+Z for Add Sub-Category (works on all vendor pages)
        if (event.altKey && (key === 'z' || key === 'Z')) {
          // Only trigger if not typing in input fields
          if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.isContentEditable) {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          // Navigate to menu page and open add sub-category modal
          navigate('/vendor/menu');
          setTimeout(() => {
            const addSubCategoryButton = document.querySelector('[data-add-sub-category-btn]');
            if (addSubCategoryButton) {
              addSubCategoryButton.click();
            } else {
              window.dispatchEvent(new CustomEvent('openAddSubCategoryModal'));
            }
          }, 100);
          return;
        }
        
        // Alt+I for Add Menu Item (works on all vendor pages)
        if (event.altKey && (key === 'i' || key === 'I')) {
          // Only trigger if not typing in input fields
          if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.isContentEditable) {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          // Navigate to menu page and open add menu item modal
          navigate('/vendor/menu');
          setTimeout(() => {
            const addMenuItemButton = document.querySelector('[data-add-menu-item-btn]');
            if (addMenuItemButton) {
              addMenuItemButton.click();
            } else {
              window.dispatchEvent(new CustomEvent('openAddMenuItemModal'));
            }
          }, 100);
          return;
        }
        
        
        // Only process shortcuts with Alt key for vendor
        if (!event.altKey) {
          return;
        }
        
        switch (key) {
          case 'b': // Billing
            event.preventDefault();
            navigate('/vendor/billing');
            break;
          case 'd': // Dashboard
            event.preventDefault();
            navigate('/vendor/dashboard');
            break;
          case 'o': // Orders
            event.preventDefault();
            navigate('/vendor/orders');
            break;
          case 't': // Open Tables
            event.preventDefault();
            navigate('/vendor/open-tables');
            break;
          case 'm': // Menu Management
            event.preventDefault();
            navigate('/vendor/menu');
            break;
          case 'q': // Tables & QR
            event.preventDefault();
            navigate('/vendor/qr-codes');
            break;
          case 'c': // Captains
            event.preventDefault();
            navigate('/vendor/captains');
            break;
          case 's': // Stock Management
            event.preventDefault();
            navigate('/vendor/stock');
            break;
          case 'v': // Inventory Management
            event.preventDefault();
            navigate('/vendor/inventory');
            break;
          case 'r': // Room Management
            event.preventDefault();
            navigate('/vendor/room-management');
            break;
          case 'b': // Booking Management
            event.preventDefault();
            navigate('/vendor/booking-management');
            break;
          case 'k': // Banquet
            event.preventDefault();
            navigate('/vendor/banquet');
            break;
          case 'u': // Coupons
            event.preventDefault();
            navigate('/vendor/coupons');
            break;
          case 'n': // Notifications
            event.preventDefault();
            navigate('/vendor/notification');
            break;
          case 'w': // Expenses
            event.preventDefault();
            navigate('/vendor/expenses');
            break;
          case 'p': // Reports (only if can view reports)
            if (canViewReports) {
              event.preventDefault();
              navigate('/vendor/reports');
            }
            break;
          case 'x': // Subscription
            event.preventDefault();
            navigate('/vendor/subscription');
            break;
          case 'h': // Subscription History
            event.preventDefault();
            navigate('/vendor/subscription-history');
            break;
          case 'f': // Profile
            event.preventDefault();
            navigate('/vendor/profile');
            break;
          case 'g': // Settings
            event.preventDefault();
            navigate('/vendor/settings');
            break;
        }
      }
      
      // Close mobile sidebar if open
      if (isMobileOpen) {
        handleMobileToggle(false);
      }
    };

    document.addEventListener('keydown', handleKeyPress, true); // Use capture mode
    
    return () => {
      document.removeEventListener('keydown', handleKeyPress, true);
    };
  }, [navigate, user, isMobileOpen, canViewReports]);

  // Update external state when local state changes
  const handleCollapseToggle = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    localStorage.setItem('sidebar-collapsed', newCollapsed);
    if (setExternalCollapsed) {
      setExternalCollapsed(newCollapsed);
    }
  };

  const handleMobileToggle = (open) => {
    setIsMobileOpen(open);
    if (setExternalMobileOpen) {
      setExternalMobileOpen(open);
    }
  };
  const [notificationCount, setNotificationCount] = useState(0);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
  const [pendingVendorsCount, setPendingVendorsCount] = useState(0);
  const [pendingDeliveryRequestsCount, setPendingDeliveryRequestsCount] = useState(0);
  const [pendingPlanRequestsCount, setPendingPlanRequestsCount] = useState(0);
  const [SuperAdminNotifications, setSuperAdminNotification] = useState(0);
  const [openTableCount,setOpenTableCount] = useState(0);
  const [pendingMenuApprovalsCount, setPendingMenuApprovalsCount] = useState(0);

  // Fetch vendor report permissions
  const fetchReportPermissions = async () => {
    if (!user || user.role !== 'vendor') return;
    try {
      const res = await api.get('/vendor/report-permissions');
      if (res.data.success) {
        setCanViewReports(res.data.data.canViewReports || false);
      }
    } catch (err) {
      console.error('Failed to fetch report permissions:', err.message);
    }
  };

  // Fetch unread notifications count from API
  const fetchUnreadCount = async () => {
    if (!user) return;
    try {
      const res = await api.get('/notifications/unread-count');
      if (res.data.success) {
        setNotificationCount(res.data.data.count);
      }
    } catch (err) {
      console.error('Failed to fetch unread notifications:', err.message);
    }
  };

  const fetchOpenTableCount = async () => {
    if (!user) return;
    try {
      const res = await api.get('/vendor/open-tables');
      if (res.data.success) {
        setOpenTableCount(res.data.count);
      }
    } catch (err) {
      console.error('Failed to fetch unread notifications:', err.message);
    }
  };

  const fetchPendingOrderCount = async () => {
    if (!user) return;
    try {
      const res = await api.get('/vendor/orders/pending-counts');
      if (res.data.success) {
        setPendingOrdersCount(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch pending orders:', err.message);
    }
  };

  const fetchSuperAdminCounts = async () => {
    if (!user || user.role !== 'superadmin') return;
    try {
      // Fetch pending vendors count
      const vendorsRes = await api.get('/superadmin/vendors/pending?limit=1');
      if (vendorsRes.data.success) {
        setPendingVendorsCount(vendorsRes.data?.total);
      }

      // Fetch pending delivery requests count
      const deliveryRes = await api.get('/superadmin/delivery-setting-requests?status=pending&limit=1');
      if (deliveryRes.data.success) {
        console.log('📊 Pending delivery requests count:', deliveryRes.data?.pagination?.total);
        setPendingDeliveryRequestsCount(deliveryRes.data?.pagination?.total);
      }

      // Fetch pending plan requests count
      const planRes = await api.get('/superadmin/plan-requests');
      if (planRes.data.success) {
        const pendingPlans = planRes.data.data.filter(req => req.status === 'pending');
        setPendingPlanRequestsCount(pendingPlans.length);
      }
    } catch (err) {
      console.error('Failed to fetch super admin counts:', err.message);
    }
  };

  const fetchPendingMenuApprovalsCount = async () => {
    if (!user || user.role !== 'vendorOwner') return;
    try {
      const res = await api.get('/vendor-owner/dashboard/pending-approvals');
      if (res.data.success) {
        setPendingMenuApprovalsCount(res.data.data.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch pending menu approvals:', err.message);
    }
  };

  const fetchPendingBillApprovalsCount = async () => {
    if (!user || user.role !== 'vendorOwner') return;
    try {
      const res = await api.get('/vendor-owner/bill-update-requests?status=pending&limit=1');
      if (res.data.success) {
        setPendingBillApprovalsCount(res.data.count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch pending bill approvals:', err.message);
    }
  };

  const fetchPendingBillUpdateRequestsCount = async () => {
    if (!user || user.role !== 'vendor') return;
    try {
      const res = await api.get('/vendor/bill-update-requests?status=pending&limit=1');
      if (res.data.success) {
        setPendingBillUpdateRequestsCount(res.data.count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch pending bill update requests:', err.message);
    }
  };

  const fetchSuperAdminNotificationCount = async () => {
    if (!user || user.role !== 'superadmin') return;
    try {
      const res = await api.get('/superadmin/notifications/stats');
      if (res.data.success) {
        setSuperAdminNotification(res.data.data?.unread || 0);
      }
    } catch (err) {
      console.error('Failed to fetch super admin notifications count:', err.message);
    }
  };



  const isNotificationSupported = () => {
  return (
    typeof window !== "undefined" &&
    "Notification" in window
  );
};

const showNotification = (data) => {
  if (!isNotificationSupported()) return;
  if (Notification.permission !== "granted") return;

  const notification = new Notification(
    data?.title || "New Order 🚀",
    {
      body: data?.message || "You have a new notification",
      icon: "/logo1.jpg",
    }
  );

  notification.onclick = () => {
    window.focus();
    window.location.href = `${process.env.REACT_APP_CRM_URL}/vendor/orders`;
    notification.close();
  };
};


useEffect(() => {
  if (!user) return;

  /* ================= INITIAL FETCH ================= */

  fetchUnreadCount();

  if (user.role === "vendor") {
    fetchPendingOrderCount();
    fetchOpenTableCount();
    fetchReportPermissions();
    fetchPendingBillUpdateRequestsCount();
  } else if (user.role === "superadmin") {
    fetchSuperAdminCounts();
    fetchSuperAdminNotificationCount();
  } else if (user.role === "vendorOwner") {
    fetchPendingMenuApprovalsCount();
    fetchPendingBillApprovalsCount();
  }

  /* ================= SOCKET JOIN ================= */

  if (user.role === "vendor") {
    socket.emit("vendor:join", `${user.id}`);
  } else if (user.role === "superadmin") {
    socket.emit("superadmin:join", user.id);
  }

  /* ================= VENDOR LISTENERS ================= */

  if (user.role === "vendor") {
    socket.on("notification:new", (payload) => {
      fetchUnreadCount()
      fetchOpenTableCount()
      // 🔔 SAFE notification
      showNotification(payload);
      if (payload.type === "NEW_ORDER") {
        setPendingOrdersCount((prev) => prev + 1);
      }
    });

    socket.on("notification:planExpiryReminder", fetchUnreadCount)
    socket.on("table:closed", ()=>{
      fetchOpenTableCount()
      fetchPendingOrderCount()
    })

    

    socket.on("notification:read", () => {
      fetchUnreadCount()
    });

    socket.on("pendingOrders:countUpdated", (payload) => {
      fetchPendingOrderCount()
    });

    socket.on("deliveryRequest:approved", (payload) => {
      fetchUnreadCount()
      showNotification({
        title: "Delivery Request Approved",
        message: payload.message,
      });
    });

    socket.on("deliveryRequest:rejected", (payload) => {
      fetchUnreadCount()
      showNotification({
        title: "Delivery Request Rejected",
        message: payload.message,
      });
    });

    socket.on("planRequest:approved", (payload) => {
      fetchUnreadCount()
      showNotification({
        title: "Plan Request Approved",
        message: payload.message,
      });
    });

    socket.on("planRequest:rejected", (payload) => {
      fetchUnreadCount()
      showNotification({
        title: "Plan Request Rejected",
        message: payload.message,
      });
    });
  }

  /* ================= SUPERADMIN LISTENERS ================= */

  if (user.role === "superadmin") {
    socket.on("vendor:newRegistration", (payload) => {
      console.log("vendor:newRegistration-sidebar")
      setPendingVendorsCount(payload.pendingCount);
      fetchSuperAdminNotificationCount();
      showNotification({
        title: "New Vendor Registration",
        message: payload.message,
      });
    });

    socket.on("deliveryRequest:new", (payload) => {
      // setPendingDeliveryRequestsCount(payload.pendingCount);
      fetchSuperAdminCounts()
      fetchSuperAdminNotificationCount();
      showNotification({
        title: "New Delivery Request",
        message: payload.message,
      });
    });

    socket.on("planRequest:new", (payload) => {
      setPendingPlanRequestsCount((prev) => prev + 1);
      fetchSuperAdminNotificationCount();
      showNotification({
        title: "New Plan Request",
        message: payload.message,
      });
    });

    socket.on("vendor:documentsUpdated", (payload) => {
      fetchSuperAdminNotificationCount();
      showNotification({
        title: "📄 Vendor Documents Updated",
        message: payload.message,
      });
    });

    socket.on("deliveryRequest:statusUpdated", (payload) => {
      fetchSuperAdminCounts()
    });

    socket.on("superadmin:notificationsCount", (payload) => {
      fetchSuperAdminNotificationCount();
    });

    
    // socket.on("superadmin:notificationsCount", fetchSuperAdminNotificationCount);
    socket.on("planRequest:statusUpdated", fetchSuperAdminCounts);
    socket.on("vendor:approved", fetchSuperAdminCounts);
    socket.on("vendor:rejected", fetchSuperAdminCounts);
  }

  /* ================= VENDOR OWNER LISTENERS ================= */

  if (user.role === "vendorOwner") {
    socket.on("billUpdateRequest", (payload) => {
      fetchPendingBillApprovalsCount();
      fetchPendingMenuApprovalsCount();
      showNotification({
        title: "New Bill Update Request",
        message: `Bill update request from ${payload.vendorName} for order ${payload.orderNumber}`,
      });
    });

    socket.on("billUpdateRequestCancelled", (payload) => {
      fetchPendingBillApprovalsCount();
    });

    socket.on("billUpdateApproved", (payload) => {
      fetchPendingBillApprovalsCount();
    });

    socket.on("billUpdateRejected", (payload) => {
      fetchPendingBillApprovalsCount();
    });
  }

  /* ================= CLEANUP ================= */

  // No cleanup needed - Sidebar is a permanent component
  // socket.off() removed to prevent intermittent issues

}, [user]);


  const superAdminMenu = [
    { path: '/superadmin/dashboard', icon: <FiHome />, label: 'Dashboard', shortcut: 'Alt+A' },
    { path: '/superadmin/vendors', icon: <FiUsers />, label: 'All Vendors', shortcut: 'Alt+V' },
    { 
      path: '/superadmin/vendors/pending', 
      icon: <FiUsers />, 
      label: 'Pending Vendors',
      shortcut: 'Alt+P',
      badge: pendingVendorsCount > 0 ? pendingVendorsCount : null
    },
    { path: '/superadmin/vendor-owners', icon: <FiUserCheck />, label: 'Vendor Owners', shortcut: 'Alt+O' },
    { 
      path: '/superadmin/delivery-requests', 
      icon: <FiUserCheck />, 
      label: 'Delivery Requests',
      shortcut: 'Alt+D',
      badge: pendingDeliveryRequestsCount > 0 ? pendingDeliveryRequestsCount : null
    },
    { path: '/superadmin/app-users', icon: <FiSmartphone />, label: 'App Users', shortcut: 'Alt+U' },
    { path: '/superadmin/room-management', icon: <FiGrid />, label: 'Room Management', shortcut: 'Alt+R' },
    { path: '/superadmin/banquet', icon: <FiPackage />, label: 'Banquet Management', shortcut: 'Alt+M' },
    { path: '/superadmin/banners', icon: <FiImage />, label: 'Banner Management', shortcut: 'Alt+B' },
    { path: '/superadmin/subscription-plans', icon: <FiCreditCard />, label: 'Subscription Plans', shortcut: 'Alt+S' },
    { 
      path: '/superadmin/plan-requests', 
      icon: <FiFileText />, 
      label: 'Plan Requests',
      shortcut: 'Alt+L',
      badge: pendingPlanRequestsCount > 0 ? pendingPlanRequestsCount : null
    },
    {
      label: 'Team Management',
      icon: <FiUsers />,
      path: '/superadmin/sales-officers',
      shortcut: 'Alt+T',
      badge: null
    },
    {
      label: 'Restaurant Visits',
      icon: <FiMapPin />,
      path: '/superadmin/restaurant-visits',
      shortcut: 'Alt+I',
      badge: null
    },
    {
      label: 'Job Management',
      icon: <FiBriefcase />,
      path: '/superadmin/jobs',
      shortcut: 'Alt+J',
      badge: null
    },
    { 
      path: '/superadmin/notifications', 
      icon: <FiBell />, 
      label: 'Notifications',
      shortcut: 'Alt+N',
      badge: SuperAdminNotifications > 0 ? SuperAdminNotifications : null
    },
    { 
      path: '/superadmin/profile', 
      icon: <FiUser />, 
      label: 'Profile',
      shortcut: 'Alt+P',
      badge: null
    },
    // { path: '/superadmin/settings', icon: <FiSettings />, label: 'System Settings' }
  ];

  const vendorMenu = [
    { path: '/vendor/billing', icon: <TbCurrencyRupee />, label: 'Billing', shortcut: 'Alt+B' },
    { path: '/vendor/dashboard', icon: <FiHome />, label: 'Dashboard', shortcut: 'Alt+D' },
    { path: '/vendor/orders', 
      icon: <FiShoppingBag />, 
      label: `Orders`,
      shortcut: 'Alt+O',
      badge: pendingOrdersCount > 0 ? pendingOrdersCount : null },
    { path: '/vendor/bill-update-requests', 
      icon: <FiCreditCard />, 
      label: 'Bill Requests', 
      shortcut: 'Alt+R',
      badge: pendingBillUpdateRequestsCount > 0 ? pendingBillUpdateRequestsCount : null },
    { 
      path: '/vendor/open-tables', 
      icon: <FiGrid />, 
      label: 'Open Tables', 
      shortcut: 'Alt+T',
      badge: openTableCount > 0 ? openTableCount : null
    },
    { path: '/vendor/menu', icon: <FiFileText />, label: 'Menu Management', shortcut: 'Alt+M' },
    { path: '/vendor/qr-codes', icon: <FiGrid />, label: 'Tables & QR', shortcut: 'Alt+Q' },
    { path: '/vendor/captains', icon: <FiUsers />, label: 'Captains', shortcut: 'Alt+C' },
    { path: '/vendor/stock', icon: <FiUsers />, label: 'Stock Management', shortcut: 'Alt+S' },
    { path: '/vendor/inventory', icon: <FiUsers />, label: 'Inventory Management', shortcut: 'Alt+I' },
    { path: '/vendor/room-management', icon: <FiKey />, label: 'Room Management', shortcut: 'Alt+R' },
    { path: '/vendor/booking-management', icon: <FiCalendar />, label: 'Booking Management', shortcut: 'Alt+B' },
    { path: '/vendor/banquet', icon: <FiMapPin />, label: 'Banquet', shortcut: 'Alt+K' },
    { path: '/vendor/coupons', icon: <FiTag />, label: 'Coupons', shortcut: 'Alt+U' },
    { path: '/vendor/notification', 
      icon: <FiTag />, 
      label: `Notifications`,
      shortcut: 'Alt+N',
      badge: notificationCount > 0 ? notificationCount : null
    },
    { path: '/vendor/expenses', icon: <TbCurrencyRupee />, label: 'Expenses', shortcut: 'Alt+E' },
    ...(canViewReports ? [
      { path: '/vendor/reports', icon: <FiTrendingUp />, label: 'Reports', shortcut: 'Alt+P' }
    ] : []),
    { path: '/vendor/subscription', icon: <FiCreditCard />, label: 'Subscription', shortcut: 'Alt+X' },
    { path: '/vendor/subscription-history', icon: <FiFileText />, label: 'Subscription History', shortcut: 'Alt+H' },
    { path: '/vendor/profile', icon: <FiUser />, label: 'Profile', shortcut: 'Alt+F' },
    { path: '/vendor/settings', icon: <FiSettings />, label: 'Settings', shortcut: 'Alt+G' }
  ];

  const captainMenu = [
    { path: '/dashboard', icon: <FiHome />, label: 'Dashboard' },
    // { path: '/orders', icon: <FiShoppingBag />, label: 'Orders' },
    // { path: '/tables', icon: <FiGrid />, label: 'My Tables' },
    // { path: '/open-tables', icon: <FiGrid />, label: 'Open Tables' }
  ];

  const vendorOwnerMenu = [
    { path: '/vendor-owner/dashboard', icon: <FiHome />, label: 'Dashboard', shortcut: 'Alt+W' },
    { path: '/vendor-owner/restaurants', icon: <FiShoppingBag />, label: 'My Restaurants', shortcut: 'Alt+R' },
    { 
      path: '/vendor-owner/menu-management', 
      icon: <FiFileText />, 
      label: 'Menu Management',
      shortcut: 'Alt+M',
      badge: pendingMenuApprovalsCount > 0 ? pendingMenuApprovalsCount : null
    },
    { path: '/vendor-owner/vendor-permissions', icon: <FiSettings />, label: 'Vendor Permissions', shortcut: 'Alt+P' },
    { path: '/vendor-owner/bill-update-approvals', icon: <FiCreditCard />, label: 'Bill Approvals', shortcut: 'Alt+B', badge: pendingBillApprovalsCount > 0 ? pendingBillApprovalsCount : null },
    // { path: '/vendor-owner/sales', icon: <TbCurrencyRupee />, label: 'Sales Report', shortcut: 'Alt+S' },
    // { path: '/vendor-owner/analytics', icon: <FiTrendingUp />, label: 'Analytics', shortcut: 'Alt+A' },
    { path: '/vendor-owner/reports', icon: <FiTrendingUp />, label: 'Reports', shortcut: 'Alt+X' },
    // { path: '/vendor-owner/orders', icon: <FiPackage />, label: 'All Orders', shortcut: 'Alt+O' },
    // { path: '/vendor-owner/settings', icon: <FiSettings />, label: 'Settings' }
  ];

  const getMenu = () => {
    switch (user?.role) {
      case 'superadmin':
        return superAdminMenu;
      case 'vendor':
        return vendorMenu;
      case 'vendorOwner':
        return vendorOwnerMenu;
      case 'captain':
        return captainMenu;
      default:
        return [];
    }
  };

  const getSidebarClass = () => {
    let className = 'sidebar';
    if (isCollapsed) className += ' collapsed';
    if (isMobileOpen) className += ' mobile-open';
    if (user?.role === 'vendor') className += ' vendor';
    if (user?.role === 'vendorOwner') className += ' vendor-owner';
    if (user?.role === 'captain') className += ' captain';
    return className;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menu = getMenu();

  return (
    <>
      {/* Sidebar Overlay for Mobile */}
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-200"
          onClick={() => handleMobileToggle(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 z-50
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        ${isCollapsed ? 'lg:w-20' : 'lg:w-64'}
        w-64
        bg-white border-r border-gray-200 h-screen
        transition-all duration-300 ease-in-out
        flex flex-col
        lg:translate-x-0 lg:fixed lg:z-40
      `}>
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="/images/logo1.jpg" 
                alt="Logo" 
                className="w-10 h-10 rounded-lg object-cover"
              />
              {!isCollapsed && (
                <div>
                  <h2 className="text-lg font-bold text-gray-800">HUKAM MERE AAKA</h2>
                  {/* <p className="text-xs text-gray-500 uppercase tracking-wide">{user?.role}</p> */}
                </div>
              )}
            </div>
            <button 
              className="hidden lg:flex p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              onClick={handleCollapseToggle}
            >
              {isCollapsed ? <FiMenu className="text-gray-600" /> : <FiX className="text-gray-600" />}
            </button>
          </div>
        </div>
        
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-3">
            {menu.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`
                    flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200
                    ${location.pathname === item.path 
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600' 
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }
                    ${isCollapsed ? 'justify-center' : 'justify-start'}
                  `}
                  onClick={() => handleMobileToggle(false)}
                  title={isCollapsed ? item.label : ''}
                >
                  <span className={`
                    ${location.pathname === item.path ? 'text-blue-600' : 'text-gray-500'}
                    ${isCollapsed ? 'text-lg' : 'text-base'}
                    flex-shrink-0
                  `}>
                    {item.icon}
                  </span>
                  {!isCollapsed && (
                    <span className="ml-3 flex-1 flex items-center justify-between">
                      {item.label}
                      <div className="flex items-center gap-2">
                        {item.shortcut && (
                          <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded font-mono">
                            {item.shortcut}
                          </span>
                        )}
                        {item.badge && (
                          <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                            {item.badge}
                          </span>
                        )}
                      </div>
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-gray-200">
          <button 
            onClick={handleLogout}
            className={`
              w-full flex items-center px-3 py-2.5 rounded-lg text-sm font-medium
              text-red-600 hover:bg-red-50 transition-all duration-200
              ${isCollapsed ? 'justify-center' : 'justify-start'}
            `}
            title={isCollapsed ? 'Logout' : ''}
          >
            <FiLogOut className="text-lg flex-shrink-0" />
            {!isCollapsed && <span className="ml-3">Logout</span>}
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
